/** @jsxImportSource react */
import { generateAskAgentQuestionContents } from '@common/helpers/agents';
import SlyEvent from '@common/helpers/events';
import { recordEntityCta } from '@common/helpers/localStorage';
import { getAgentUrl } from '@common/helpers/url';
import Thankyou from '@react/cta/components/Thankyou';
import redirectToHomebase from '@react/cta/helpers/redirectToHomebase';
import Avatar from '@react/shared/components/Avatar';
import ModalTray from '@react/shared/components/ModalTray';
import { HeaderWithClose } from '@react/shared/components/NewModal';
import { Link } from '@react/system';
import { AGENT_ASK_QUESTIONS } from '@sly/core/constants/actionTypes';
import {
  AGENT_ENTITY_TYPE,
  PROPERTY_ENTITY_TYPE,
} from '@sly/core/constants/entityTypes';
import dynamic from 'next/dynamic';
import { func, object, string } from 'proptypes';
import { useCallback, useEffect, useState } from 'react';

const possibleModals = {
  thankYouModal: 'ThankYou',
  askQuestionToAgentModal: 'AskQuestionToAgentFormContainer',
};

const AskQuestionToAgentFormContainer = dynamic(() =>
  import(
    /* webpackChunkName: "chunkAskQuestionToAgentFormContainer" */ '@react/cta/components/AskQuestionToAgentFormContainer'
  )
);

// TODO - create generic ask agent question cta
const AskAgentQuestionContainer = ({
  type = 'expert',
  children,
  agent,
  afterModalClose,
  afterSubmit,
  community,
  openByDefault = false,
  mode,
  initialMessage,
  uuidData,
}) => {
  const [modalProps, setModalProps] = useState(null);

  const displayName = agent?.info?.displayName;
  let path;
  if (agent?.gallery?.images?.length) {
    path = agent?.gallery.images[0].path;
  }

  const onCloseModal = useCallback(
    (beforeCloseModal) => () => {
      beforeCloseModal?.();
      setModalProps(null);
      if (afterModalClose) {
        afterModalClose();
      }
    },
    []
  );

  const handleToggleAskAgentQuestionModal = (
    isAskAgentQuestionModalVisible,
    subType
  ) => {
    const action = isAskAgentQuestionModalVisible
      ? 'close-modal'
      : 'open-modal';
    let category = 'AskAgentQuestion';

    if (subType && typeof subType === 'string') category += `-${subType}`;

    const event = { action, category, label: 'get-expert-help' };

    SlyEvent.getInstance().sendEvent(event);
  };

  const openAskAgentQuestionModal = useCallback(
    (subType) => {
      const toggleAskAgentQuestionModal = () => {
        handleToggleAskAgentQuestionModal(true, subType);
      };
      const beforeCloseModal = () =>
        handleToggleAskAgentQuestionModal(true, subType);

      const postSubmit = (shouldRedirect) => {
        toggleAskAgentQuestionModal();
        if (community) recordEntityCta(type, community.id);

        afterSubmit?.();
        if (shouldRedirect) {
          redirectToHomebase(community?.name, { entry: 'speakExpert' });
        } else {
          setModalProps({ name: possibleModals.thankYouModal });
        }
      };

      const askQuestionModalDefaultProps = {
        name: possibleModals.askQuestionToAgentModal,
        beforeCloseModal,
        postSubmit,
        type,
      };

      if (community) {
        askQuestionModalDefaultProps.entityId = community.id;
        askQuestionModalDefaultProps.entityType = PROPERTY_ENTITY_TYPE;
        askQuestionModalDefaultProps.entityName = community.name;
      } else if (agent) {
        askQuestionModalDefaultProps.entityId = agent.id;
        askQuestionModalDefaultProps.entityType = AGENT_ENTITY_TYPE;
        askQuestionModalDefaultProps.entityName = agent?.info?.displayName;
      }

      if (community) {
        const { heading, description, placeholder, question } =
          generateAskAgentQuestionContents(
            community.name,
            community.address.city,
            type
          );
        setModalProps({
          toggleAskAgentQuestionModal,
          heading,
          description,
          placeholder,
          question,
          actionType: AGENT_ASK_QUESTIONS,
          agentSlug: agent?.id,
          agentName: agent?.info?.displayName,
          ...askQuestionModalDefaultProps,
        });
      } else if (agent) {
        setModalProps({
          toggleAskAgentQuestionModal,

          actionType: AGENT_ASK_QUESTIONS,
          agentSlug: agent?.id,
          agentName: agent?.info?.displayName,
          ...askQuestionModalDefaultProps,
        });
      }

      handleToggleAskAgentQuestionModal(false, subType);
    },
    [type, community, handleToggleAskAgentQuestionModal]
  );

  const {
    name: modalName,
    beforeCloseModal,
    ...restModalProps
  } = modalProps || {};

  useEffect(() => {
    if (openByDefault) {
      openAskAgentQuestionModal();
    }
  }, []);

  return (
    <>
      {children(openAskAgentQuestionModal)}
      {!!modalProps && (
        <ModalTray
          isOpen={!!modalProps}
          onClose={onCloseModal(beforeCloseModal)}
        >
          <HeaderWithClose onClose={onCloseModal()}>
            Get help from an expert
          </HeaderWithClose>
          <div data-tp-id='gen-div-9e90269e-5b96-4f6d-a3f3-77065c172271'>
            {modalName === possibleModals.thankYouModal && (
              <Thankyou
                heading='Success!'
                subheading='Your request has been sent and we will connect with you shortly.'
                onClose={onCloseModal()}
                doneText='Finish'
              />
            )}
            {modalName === possibleModals.askQuestionToAgentModal && (
              <>
                {agent && (
                  <div className='grid grid-cols-[auto_1fr] items-center gap-2 px-6 pt-6'>
                    <div className='h-[40px] w-[40px]'>
                      <Link to={getAgentUrl(agent)}>
                        <Avatar size='m' path={path} name={displayName} />
                      </Link>
                    </div>
                    <div className='flex flex-col justify-center text-left'>
                      <Link
                        to={getAgentUrl(agent)}
                        className='font-t-xs-azo !text-slate-base'
                      >
                        {displayName}
                      </Link>
                      <div className='font-b-s text-slate-base'>
                        Seniorly Advisor
                      </div>
                    </div>
                  </div>
                )}
                <AskQuestionToAgentFormContainer
                  {...restModalProps}
                  initialMessage={initialMessage}
                  mode={mode}
                  uuidData={uuidData}
                />
              </>
            )}
          </div>
        </ModalTray>
      )}
    </>
  );
};

AskAgentQuestionContainer.typeHydrationId = 'AskAgentQuestionContainer';

AskAgentQuestionContainer.propTypes = {
  type: string,
  children: func,
  agent: object,
  afterModalClose: func,
};

export default AskAgentQuestionContainer;
